import { HUD_DISPLAY_FY } from "@/data/manual-facts";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand } from "@/components/site/Blocks";
import { states } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import heroIncomeLimits from "@/assets/hero/hero-income-limits.jpg.asset.json";

const HERO = {
  src: heroIncomeLimits.url,
  alt: "Well-kept brick four-unit townhome building at dusk with warm lit windows, household income eligibility documents and a calculator in the foreground — HUD income limits for Section 8 rental property investors",
};

export const Route = createFileRoute("/hud-income-limits/")({
  head: () => {
    const url = "/hud-income-limits";
    const title = `${HUD_DISPLAY_FY} HUD Income Limits by State | Simply Approved`;
    const description =
      `Browse ${HUD_DISPLAY_FY} HUD Section 8 Income Limits (30/50/80% AMI) by state and county. From HUD's official published tables — for LIHTC underwriting and voucher eligibility.`;
    return buildHead({
      title,
      description,
      url,
      keywords:
        "HUD income limits 2026, Section 8 income limits by state, AMI income thresholds, LIHTC income limits, extremely low income limit, very low income limit, low income limit HUD, HUD income limits lookup, area median income by county, Section 8 eligibility income, HUD income limits API, income limits by household size",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "HUD Income Limits", url },
      ],
      service: { name: "HUD Income Limits Lookup" },
      image: HERO.src,
      imageAlt: HERO.alt,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: `${HUD_DISPLAY_FY} HUD Income Limits — All States`,
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/datasets/il.html",
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: IlHub,
});

function IlHub() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD data"
        title={`${HUD_DISPLAY_FY} HUD Income Limits`}
        subtitle="Pick a state to see every county's 30% / 50% / 80% AMI income limits — the same numbers HUD publishes for Section 8 eligibility and LIHTC compliance."
        primaryLabel="Start My Approval"
        primaryHref="/apply"
        secondaryLabel="See Today's Rates"
        secondaryHref="/rates"
        image={HERO}
      />
      <Section>
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-2xl font-bold text-foreground">Income Limits by state</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Every state where we serve investors. Each page pulls live from HUD's Income Limits API and lists Very Low,
            Extremely Low, and Low Income thresholds by household size (1–8 persons). Lending is licensed in Florida and Colorado; LLC business-purpose loans in other states where permitted.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {states.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/hud-income-limits/$state"
                  params={{ state: s.slug }}
                  className="block rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:border-brand hover:text-brand"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title="Pricing a LIHTC or Section 8 acquisition?"
        body="Send the county and unit mix — we'll DSCR-underwrite against HUD FMR and confirm tenant income eligibility against these AMI thresholds."
      />
    </SiteShell>
  );
}
